import React, { useEffect, useState } from 'react';
import { transferService, Transfer } from '../services/transferService';
import { useAuth } from '../contexts/AuthContext';
import { maskCPF } from '../utils/security';
import './Transfers.css';

const Transfers: React.FC = () => {
  const { user } = useAuth();
  const [transfers, setTransfers] = useState<{ sent: Transfer[]; received: Transfer[] }>({
    sent: [],
    received: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTransfers();
  }, []);

  const loadTransfers = async () => {
    try {
      setLoading(true);
      const data = await transferService.getTransfers();
      setTransfers(data);
    } catch (error: any) {
      console.error('Erro ao carregar transferências:', error);
      if (error.response) {
        if (error.response.status === 401) {
          // Token inválido - será tratado pelo interceptor
          return;
        }
        alert(error.response.data?.error || 'Erro ao carregar transferências');
      } else if (error.request) {
        alert('Erro ao conectar com o servidor. Verifique sua conexão.');
      } else {
        alert('Erro ao carregar transferências. Tente novamente.');
      }
      setTransfers({ sent: [], received: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: number) => {
    if (!window.confirm('Deseja aprovar esta transferência?')) {
      return;
    }

    try {
      await transferService.approveTransfer(id);
      loadTransfers();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao aprovar transferência');
    }
  };

  const handleCancel = async (id: number) => {
    if (!window.confirm('Deseja cancelar esta transferência?')) {
      return;
    }

    try {
      await transferService.cancelTransfer(id);
      loadTransfers();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao cancelar transferência');
    }
  };

  const handleReject = async (id: number) => {
    if (!window.confirm('Deseja recusar esta transferência?')) {
      return;
    }

    try {
      await transferService.cancelTransfer(id);
      loadTransfers();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao recusar transferência');
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
      pending: 'Pendente',
      completed: 'Concluída',
      cancelled: 'Cancelada',
    };
    return labels[status] || status;
  };

  const getStatusClass = (status: string) => {
    const classes: { [key: string]: string } = {
      pending: 'status-pending',
      completed: 'status-completed',
      cancelled: 'status-cancelled',
    };
    return classes[status] || '';
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  return (
    <div className="container">
      <h1>Transferências</h1>

      <div className="transfers-section">
        <div className="card">
          <h2>Transferências Recebidas</h2>
          {transfers.received.length === 0 ? (
            <p>Nenhuma transferência recebida</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Animal</th>
                  <th>De</th>
                  <th>Data da Solicitação</th>
                  <th>Motivo</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {transfers.received.map((transfer) => (
                  <tr key={transfer.id}>
                    <td>
                      {transfer.animal?.name || `Animal #${transfer.animal_id}`}
                      {transfer.animal && (
                        <div className="animal-details">
                          <small>{transfer.animal.species} - {transfer.animal.breed || 'Sem raça'}</small>
                        </div>
                      )}
                    </td>
                    <td>
                      {transfer.from_responsible?.name || transfer.from_user?.name || 'N/A'}
                      {transfer.from_responsible?.cpf && (
                        <div className="responsible-details">
                          <small>CPF: {maskCPF(transfer.from_responsible.cpf)}</small>
                        </div>
                      )}
                    </td>
                    <td>
                      {new Date(transfer.created_at || transfer.transfer_date).toLocaleDateString('pt-BR')}
                    </td>
                    <td>
                      {transfer.reason || <em>Sem motivo informado</em>}
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusClass(transfer.status)}`}>
                        {getStatusLabel(transfer.status)}
                      </span>
                    </td>
                    <td>
                      {transfer.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(transfer.id)}
                            className="btn btn-success"
                            style={{ marginRight: '10px', marginBottom: '5px' }}
                          >
                            ✓ Aceitar
                          </button>
                          <button
                            onClick={() => handleReject(transfer.id)}
                            className="btn btn-danger"
                          >
                            ✗ Recusar
                          </button>
                        </>
                      )}
                      {transfer.status === 'completed' && (
                        <span className="text-success">Transferência concluída</span>
                      )}
                      {transfer.status === 'cancelled' && (
                        <span className="text-danger">Transferência recusada/cancelada</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card">
          <h2>Transferências Enviadas</h2>
          {transfers.sent.length === 0 ? (
            <p>Nenhuma transferência enviada</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Animal</th>
                  <th>Para</th>
                  <th>Data da Solicitação</th>
                  <th>Motivo</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {transfers.sent.map((transfer) => (
                  <tr key={transfer.id}>
                    <td>
                      {transfer.animal?.name || `Animal #${transfer.animal_id}`}
                      {transfer.animal && (
                        <div className="animal-details">
                          <small>{transfer.animal.species} - {transfer.animal.breed || 'Sem raça'}</small>
                        </div>
                      )}
                    </td>
                    <td>
                      {transfer.to_user?.name || 'N/A'}
                      {transfer.to_user?.cpf && (
                        <div className="responsible-details">
                          <small>CPF: {maskCPF(transfer.to_user.cpf)}</small>
                        </div>
                      )}
                    </td>
                    <td>
                      {new Date(transfer.created_at || transfer.transfer_date).toLocaleDateString('pt-BR')}
                    </td>
                    <td>
                      {transfer.reason || <em>Sem motivo informado</em>}
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusClass(transfer.status)}`}>
                        {getStatusLabel(transfer.status)}
                      </span>
                    </td>
                    <td>
                      {transfer.status === 'pending' && (
                        <button
                          onClick={() => handleCancel(transfer.id)}
                          className="btn btn-danger"
                        >
                          Cancelar Solicitação
                        </button>
                      )}
                      {transfer.status === 'completed' && (
                        <span className="text-success">Transferência concluída</span>
                      )}
                      {transfer.status === 'cancelled' && (
                        <span className="text-danger">Transferência cancelada</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Transfers;
